import React, { memo, useState } from 'react'
import { Text, Icon, AdvancedTooltip } from 'common-components'
import CheckIcon from 'assets/svgr-icons/Check.svg'
import ThunderIcon from 'assets/svgr-icons/Thunder.svg'
import * as CheckmarkStyled from 'common-components/DropdownItem/styled'
import * as Styled from './styled'
import { IOption } from '../OptionDropdown/types'
import { getDeviceVersion } from 'utils'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'

interface IFrameMatchingInfo {
  resizeFrameCount: number
}

interface IProps {
  options: Array<IOption>
  selectedValue: string | null
  onSelect: (value: string | null) => void
  creditsCost: number
  resizeCountByRatio: Record<string, IFrameMatchingInfo>
}

const AspectRatioItem: React.FC<{
  label: string
  labelSuffix?: string
  isSelected: boolean
  onSelect: () => void
  creditsCost?: number
  showTooltip?: boolean
}> = memo(
  ({ label, labelSuffix, isSelected, onSelect, creditsCost, showTooltip }) => {
    const [itemRef, setItemRef] = useState<HTMLDivElement | null>(null)
    const isMobile = getDeviceVersion()

    return (
      <Styled.ItemWrapper $isSelected={isSelected} onClick={onSelect}>
        <Styled.TooltipTrigger ref={setItemRef}>
          <Styled.ItemLeftContent>
            <Text
              type="subtitle"
              color={isSelected ? 'secondaryDark' : 'secondarySemiDark'}
            >
              {label}
            </Text>
            {labelSuffix && (
              <Text type="h2" color="secondary">
                {labelSuffix}
              </Text>
            )}
            {creditsCost !== undefined && (
              <Styled.ItemCredits>
                <Icon icon={<ThunderIcon />} size="small" color="secondary" />
                <Text type="h4" color="secondary">
                  {creditsCost} {translate(T.CREDITS_LABEL)}
                </Text>
              </Styled.ItemCredits>
            )}
          </Styled.ItemLeftContent>
        </Styled.TooltipTrigger>

        {isSelected && (
          <CheckmarkStyled.CheckmarkWrapper>
            <Icon icon={<CheckIcon />} size="medium" color="primaryDark" />
          </CheckmarkStyled.CheckmarkWrapper>
        )}

        {showTooltip && !isMobile && (
          <AdvancedTooltip
            btnRef={{ current: itemRef }}
            tooltipText={translate(T.AUTO_RESIZE_REF_IMAGE_TOOLTIP)}
            tooltipPosition="top"
            maxWidth={219}
            textAlign="center"
          />
        )}
      </Styled.ItemWrapper>
    )
  }
)

AspectRatioItem.displayName = 'AspectRatioItem'

const ImageAspectRatioDropdownContent: React.FC<IProps> = ({
  options,
  selectedValue,
  onSelect,
  creditsCost,
  resizeCountByRatio
}) => {
  const hideOriginal = Object.values(resizeCountByRatio).some(
    info => info.resizeFrameCount === 0
  )

  return (
    <Styled.DropdownListWrapper>
      {!hideOriginal && (
        <AspectRatioItem
          label={translate(T.ORIGINAL_LABEL)}
          labelSuffix={translate(T.ORIGINAL_REF_IMAGE_SIZE_SUFFIX_LABEL)}
          isSelected={selectedValue === null}
          onSelect={() => onSelect(null)}
        />
      )}
      {options.map(option => {
        const ratioKey = String(option.value)
        const resizeCount = resizeCountByRatio[ratioKey]?.resizeFrameCount ?? 0
        const perOptionCost =
          resizeCount === 0 ? undefined : creditsCost * resizeCount

        return (
          <AspectRatioItem
            key={ratioKey}
            label={option.label}
            isSelected={option.value === selectedValue}
            onSelect={() => onSelect(ratioKey)}
            creditsCost={perOptionCost}
            showTooltip={resizeCount > 0}
          />
        )
      })}
    </Styled.DropdownListWrapper>
  )
}

export default memo(ImageAspectRatioDropdownContent)
